import React from "react";
import { Link } from "react-router-dom";
import { ASSETS } from "../lib/api";
import { useT } from "../i18n/LanguageContext";

export const Hero = () => {
  const { t } = useT();
  const scrollToLedger = (e) => {
    e.preventDefault();
    document.querySelector("#ledger")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[88vh] overflow-hidden"
      style={{ background: "#0F1D14" }}
    >
      {/* Flag background — split black/red wash without competing crest */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, #000000 0%, #000000 45%, #1B3022 45%, #1B3022 55%, #8B0E1E 55%, #8B0E1E 100%)",
          }}
        />
        <img
          src={ASSETS.flag}
          alt="Haitian sovereign flag"
          className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-overlay"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(15,29,20,0.65) 0%, rgba(15,29,20,0.55) 30%, rgba(15,29,20,0.85) 80%, rgba(15,29,20,0.98) 100%)",
          }}
        />
        <div className="absolute inset-0 bv-grain" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32 lg:pt-36 lg:pb-40 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">
              {t("hero.tagline")}
            </span>
          </div>

          <h1
            className="font-serif-bv text-white leading-[1.05] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] text-shadow-royal"
            data-testid="hero-headline"
          >
            {t("hero.title_1")} <span className="italic font-charter-bv text-[#D4AF37] text-shadow-gold">{t("hero.title_italic")}</span> {t("hero.title_2")}
            <br />
            {t("hero.title_3")}
          </h1>

          <p
            className="mt-8 text-base sm:text-lg max-w-2xl text-[#F5EFE0]/90 fade-up delay-1 leading-relaxed text-shadow-royal"
            data-testid="hero-subheadline"
          >
            {t("hero.subtitle")}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 fade-up delay-2">
            <Link
              to="/commission"
              data-testid="hero-commission-btn"
              className="btn-gold rounded-sm px-9 py-4 text-sm inline-flex items-center justify-center"
            >
              {t("hero.commission_btn")}
            </Link>
            <a
              href="#ledger"
              onClick={scrollToLedger}
              data-testid="hero-track-btn"
              className="btn-outline-gold rounded-sm px-9 py-4 text-sm inline-flex items-center justify-center"
            >
              {t("hero.track_btn")}
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl fade-up delay-3">
            {[
              { k: "4", v: t("hero.stat_posts") },
              { k: "20+", v: t("hero.stat_years") },
              { k: "12K", v: t("hero.stat_manifests") },
            ].map((s) => (
              <div key={s.v} className="border-l border-[#D4AF37]/40 pl-4">
                <div className="font-serif-bv text-3xl text-[#D4AF37] tabular">{s.k}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#F5EFE0]/70 mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="hidden lg:flex lg:col-span-5 justify-end fade-up delay-2">
          <div className="relative">
            <div className="absolute -inset-6 rounded-full bg-[#D4AF37]/10 blur-2xl" />
            <img
              src={ASSETS.crest}
              alt="HBS — Hayti Bon Sèvis Shipping"
              className="relative max-h-[420px] w-auto object-contain bg-white rounded-sm p-6"
            />
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="absolute bottom-0 inset-x-0 z-10 border-t border-[#D4AF37]/30 bg-black/60 backdrop-blur-sm overflow-hidden">
        <div className="marquee-track py-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex shrink-0 items-center">
              {[
                "Miami → Port-au-Prince",
                "Air Freight",
                "Sea Freight",
                "Diplomatic Tracking",
                "Montréal → Cap-Haïtien",
                "Sovereign Manifests",
                "Santiago → Les Cayes",
                "55-Gallon Barrels",
                "Liberté ou la Mort",
              ].map((t, j) => (
                <span
                  key={`${i}-${j}`}
                  className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]/90 px-8 whitespace-nowrap"
                >
                  ❖ {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
